import React, { useEffect, useState } from "react";
import { api } from "../../lib/apiClient";
import { Save, Lock } from "lucide-react";
import { toast } from "../../components/ui/sonner";
import { UserPageExtras } from "./UserPageExtras";

const PAGES = [
  ["quotes", "Calculations", "Quote list, quote matrix pages and editing"],
  ["quote_create", "Create quote", "New Quote and Quick Create"],
  ["surveys", "Surveys", "Survey list and questionnaires"],
  ["users", "Users", "Managers only — their own depot's users"],
  ["profile", "Profile", "Their own details and password"],
];
const ROLES = [["manager", "Manager"], ["estimator", "Estimator"]];

export function PageAccessTab() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/page-access").then((r) => setCfg(r.data)); }, []);
  if (!cfg) return <div className="text-sm text-slate-500">Loading…</div>;

  const toggle = (role, page, on) =>
    setCfg({ ...cfg, [role]: on ? [...new Set([...cfg[role], page])] : cfg[role].filter((p) => p !== page) });

  const save = async () => {
    setSaving(true);
    try {
      setCfg((await api.put("/page-access", cfg)).data);
      toast.success("Page access saved. Users see the change next time they log in or refresh.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5 max-w-3xl" data-testid="page-access-tab">
      <p className="text-sm text-slate-600">Choose which pages each role can open. Pages that are switched off are hidden from the menu and their links are blocked.</p>
      <div className="border border-slate-200 rounded-lg bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-2.5 text-left text-[11px] uppercase tracking-wider font-semibold text-slate-500">Page</th>
              <th className="px-4 py-2.5 text-center text-[11px] uppercase tracking-wider font-semibold text-slate-500">Admin</th>
              {ROLES.map(([, l]) => <th key={l} className="px-4 py-2.5 text-center text-[11px] uppercase tracking-wider font-semibold text-slate-500">{l}</th>)}
            </tr>
          </thead>
          <tbody>
            {PAGES.map(([k, label, hint]) => (
              <tr key={k} className="border-t border-slate-100">
                <td className="px-4 py-3"><div className="font-semibold text-slate-900">{label}</div><div className="text-xs text-slate-500">{hint}</div></td>
                <td className="px-4 py-3 text-center"><input type="checkbox" checked disabled className="h-4 w-4" /></td>
                {ROLES.map(([r]) => (
                  <td key={r} className="px-4 py-3 text-center">
                    {k === "users" && r === "estimator" ? <span className="text-slate-300">—</span> : (
                      <input type="checkbox" className="h-4 w-4 accent-slate-900 cursor-pointer" checked={cfg[r].includes(k)}
                        onChange={(e) => toggle(r, k, e.target.checked)} data-testid={`page-access-${r}-${k}`} />
                    )}
                  </td>
                ))}
              </tr>
            ))}
            <tr className="border-t border-slate-100 bg-slate-50/60">
              <td className="px-4 py-3"><div className="font-semibold text-slate-900 inline-flex items-center gap-1.5"><Lock size={12} /> Settings</div><div className="text-xs text-slate-500">Always admin only</div></td>
              <td className="px-4 py-3 text-center"><input type="checkbox" checked disabled className="h-4 w-4" /></td>
              {ROLES.map(([r]) => <td key={r} className="px-4 py-3 text-center text-slate-300">—</td>)}
            </tr>
          </tbody>
        </table>
      </div>
      <button onClick={save} disabled={saving} data-testid="page-access-save"
        className="h-10 px-5 rounded-full bg-brand-blue text-white text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2 hover:bg-brand-blue-dark disabled:opacity-60">
        <Save size={14} /> {saving ? "Saving…" : "Save page access"}
      </button>
      <UserPageExtras pages={PAGES} roleCfg={cfg} />
    </div>
  );
}
